import type { CptModel } from '@/models/cpt'

type RawTrainerExtras = {
  term_locations?: unknown
  menu_order?: number
  featured_media?: number

  acf?: {
    color_theme?: string
    components?: unknown[] | false

    excerpt?: {
      icon?: string
      super_title?: string
      excerpt_text?: string
    }

    meta?: {
      show_breadcrumbs?: boolean
      breadcrumb?: number | false
      publish_date?: boolean | string
      [key: string]: unknown
    }

    person_details?: {
      image?: number
      details?: {
        name?: string
        phone?: string
        email?: string
        level?: string
        locations?: Array<{
          term_id?: number
          name?: string
          slug?: string
          taxonomy?: string
          [key: string]: unknown
        }>
      }
    }

    reviews?: {
      reviews?: {
        settings?: unknown
        reviews?: {
          title?: string
          excerpt?: string
          review_items?: Array<{
            review_content?: string
            author?: {
              first_name?: string
              last_name?: string
              initials?: string
            }
          }> | false
        }
      }
    }
  }
}

export type TrainerModel = CptModel & {
  type: 'trainer'

  menuOrder?: number
  featuredMedia?: number
  termLocations: number[]

  colorTheme?: string

  excerpt?: {
    superTitle?: string
    text?: string
    icon?: string
  }

  person?: {
    imageId?: number
    name?: string
    phone?: string
    email?: string
    level?: string
    locations: Array<{ id: number; name?: string; slug?: string }>
  }

  reviews?: {
    title?: string
    excerpt?: string
    items: Array<{
      contentHtml?: string
      author?: {
        firstName?: string
        lastName?: string
        initials?: string
      }
    }>
  }

  components?: unknown[] | false
}

const toNumberArray = (v: unknown): number[] =>
  Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : []

const trainer = (p: CptModel): TrainerModel => {
  const raw = (p.raw ?? {}) as RawTrainerExtras

  const acf = raw.acf
  const personDetails = acf?.person_details
  const person = personDetails?.details

  const personLocationsRaw = Array.isArray(person?.locations) ? person.locations : []
  const personLocations = personLocationsRaw
    .map((l) => ({
      id: typeof l.term_id === 'number' ? l.term_id : 0,
      name: typeof l.name === 'string' ? l.name : undefined,
      slug: typeof l.slug === 'string' ? l.slug : undefined,
    }))
    .filter((l) => l.id > 0)

  const reviewItemsRaw = acf?.reviews?.reviews?.reviews?.review_items
  const reviewItems = Array.isArray(reviewItemsRaw)
    ? reviewItemsRaw.map((r) => ({
        contentHtml: typeof r.review_content === 'string' ? r.review_content : undefined,
        author: r.author
          ? {
              firstName: typeof r.author.first_name === 'string' ? r.author.first_name : undefined,
              lastName: typeof r.author.last_name === 'string' ? r.author.last_name : undefined,
              initials: typeof r.author.initials === 'string' ? r.author.initials : undefined,
            }
          : undefined,
      }))
    : []

  return {
    ...p,
    type: 'trainer',

    menuOrder: raw.menu_order,
    featuredMedia: typeof raw.featured_media === 'number' ? raw.featured_media : p.media,
    termLocations: toNumberArray(raw.term_locations),

    colorTheme: acf?.color_theme,

    excerpt: acf?.excerpt
      ? {
          superTitle: acf.excerpt.super_title,
          text: acf.excerpt.excerpt_text,
          icon: acf.excerpt.icon,
        }
      : undefined,

    person: personDetails
      ? {
          imageId: typeof personDetails.image === 'number' ? personDetails.image : undefined,
          name: typeof person?.name === 'string' ? person.name : undefined,
          phone: typeof person?.phone === 'string' ? person.phone : undefined,
          email: typeof person?.email === 'string' ? person.email : undefined,
          level: typeof person?.level === 'string' ? person.level : undefined,
          locations: personLocations,
        }
      : undefined,

    reviews: acf?.reviews?.reviews?.reviews
      ? {
          title: acf.reviews.reviews.reviews.title,
          excerpt: acf.reviews.reviews.reviews.excerpt,
          items: reviewItems,
        }
      : undefined,

    components: acf?.components ?? undefined,
  }
}

export default trainer
